/* eslint-disable import/no-named-as-default-member */

import React, { useEffect, useState } from 'react';
import { useLocation, Switch, Route } from 'react-router';
import { parse } from 'query-string';
import { setCountry } from '../../../../i18n';
import { isObjectEmpty } from '../../../common/src/legacy/services/objectService';
import ReactGA from 'react-ga';
import { Helmet } from 'react-helmet';

// Components
import IntroView from '../../../sales-conversation/src/pages';
import { LoadingSpinner } from 'wg-fe-ui';

// Redux
import { useDispatch, useSelector } from 'react-redux';
import {
  setCampaignData,
  resetProductFactory,
  setShowInfoModal,
} from '../../../sales-conversation/src/store/redux/slices/productFactorySlice';

// Services
import { getBrokerData } from '../../../common/src/legacy/services/brokerDataService';
import {
  getCampaignData,
  CAMPAIGN_DATA,
  BROKER_DATA,
  getCampaignIdFromURL,
} from '../../../sales-conversation/src/services/productFactoryService';

// Types
import { Campaign } from '../../../../types/Campaigns';
import { Center } from '@wegroup/design-system';
import cogoToast from 'cogo-toast';
import { retrieveCampaignById } from '../../../common/src/legacy/services/apiRouterService';
import { setDefaultValuesFromParams } from '../../../sales-conversation/src/services/prefillService';
import { RootState } from '../../../sales-conversation/src/store/redux/reducers/rootReducer';
import Selection from './selection';
import useFeatureFlags from '../../../app/src/hooks/useFeatureFlags';
import Recommendations from './recommendations';
import useIsNLplan from '../../../common/src/hooks/useIsNLPlan';
import RecommendationsFinish from './recommendations/finalize';
import RecommendationsSuccess from './recommendations/success';
import AdvisoryFlowError from '../../../flows/src/components/ui/advisory-flow/components/app-shell/AdvisoryFlowError';
import BrokerInfoModal from '../../../flows/src/components/ui/advisory-flow/components/broker-info/BrokerInfoModal';
import CompanySuccess from './recommendations/company-success';
import CampaignsFinalizationView from './campaigns-finalization';
import DisabledModulePage from '../../../app/src/features/disabled-module-page/DisabledModulePage';
import { useTranslation } from 'react-i18next';
import useAuth from '../../../app/src/features/authentication/hooks/useAuth';
import SocialRoute from '../../../app/src/features/routing/components/SocialRoute';
import { isSocialLoginUrl } from '../../../app/src/features/authentication/constants/SocialAuth';

interface Props {
  url?: string;
}

/**
 * The main component for Risk Analysis
 */
const RiskAnalysis: React.FC<React.PropsWithChildren<Props>> = ({ url }) => {
  const { t } = useTranslation();
  const featureFlags = useFeatureFlags();
  const dispatch = useDispatch();
  const { isNL } = useIsNLplan();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { campaign, showInfoModal } = useSelector(
    (state: RootState) => state.productFactory,
  );

  const { distribution } = useAuth();

  const location = useLocation();

  const isCampaign =
    Boolean(getCampaignData()) || window?.origin?.includes('campaigns');

  const storeCampaign = (data: Campaign) => {
    //Temp
    dispatch(setCampaignData(data));
    sessionStorage.setItem(CAMPAIGN_DATA, JSON.stringify(data));
  };

  const initial = async () => {
    // Get campaigns data from session storage
    if (isCampaign) {
      await retrieveAndStoreCampaign(
        getCampaignIdFromURL(window.location.href),
      );
    } else {
      retrieveData();
      setIsLoading(false);
    }
  };

  /** Sets the default values for the campaigns data */
  useEffect(() => {
    // Check for params
    if (location.pathname === url && location.search.length > 0) {
      sessionStorage.setItem('params', JSON.stringify(parse(location.search)));
      setDefaultValuesFromParams(JSON.stringify(parse(location.search)));
    }

    initial();

    return () => {
      if (
        process.env.NODE_ENV !== 'development' &&
        process.env.REACT_APP_ENVIRONMENT !== 'QA'
      )
        dispatch(resetProductFactory());
    };
  }, []);

  useEffect(() => {
    if (!isObjectEmpty(campaign)) {
      if (campaign.google_analytics_tracking_id)
        setupGoogleAnalytics(
          campaign.short_id,
          campaign.name,
          campaign.google_analytics_tracking_id,
        );
      retrieveData(campaign);
    }
  }, [campaign]);

  useEffect(() => {
    if (campaign.google_analytics_tracking_id) {
      ReactGA.pageview(location.pathname + location.search);
    }
  }, [location]);

  const setupGoogleAnalytics = (
    campaignId: string,
    campaignName: string,
    google_analytics_tracking_id: string,
  ) => {
    ReactGA.initialize(google_analytics_tracking_id);
    ReactGA.set({
      campaignName,
      campaignId,
    });
    ReactGA.pageview(location.pathname + location.search);
  };

  /** This function collects all API calls and wait for them in a
   * Promise.all style so that they are executed concurrently and
   * meanwhile every response is awaited
   */
  const retrieveData = async (campaign?: Campaign) => {
    await retrieveBrokerData(campaign?.assigned_to || campaign?.created_by);
    setIsLoading(false);
  };

  /** Retrieve the broker data */
  const retrieveBrokerData = async (brokerId?: string) => {
    if (brokerId) sessionStorage.setItem('broker_id', brokerId);
    const data = await getBrokerData(brokerId);
    if (data) {
      if (data.broker?.country_code) setCountry(data.broker?.country_code);
      sessionStorage.setItem(BROKER_DATA, JSON.stringify(data.broker));
    }
  };

  const retrieveAndStoreCampaign = async (campaignId: string) => {
    const [resp, status] = await retrieveCampaignById(campaignId);
    if (status !== 200 && status !== 404)
      cogoToast.error('Error fetching campaign');
    storeCampaign(resp as Campaign);
  };
  if (isLoading)
    return (
      <Center width="100vw" height="100vh" data-test-id="RiskAn_RiskAnalysis">
        <LoadingSpinner />
      </Center>
    );

  if (featureFlags.isRiskAnalysisDisabled) {
    return <DisabledModulePage pageName={t('RISK-ANALYSIS')} />;
  }

  return (
    <>
      {isCampaign && (
        <Helmet>
          <title>{campaign.display_name}</title>
          <meta name="description" content={campaign.name} />
          <meta
            property="og:image"
            content={campaign.logo || distribution?.logos?.logo || ''}
          />
        </Helmet>
      )}

      {isSocialLoginUrl && (
        <BrokerInfoModal
          showModal={showInfoModal}
          setShowModal={(arg0: boolean) => dispatch(setShowInfoModal(arg0))}
        />
      )}

      <Switch>
        <Route
          path={[`${url}`]}
          render={() => (
            <IntroView
              nextUrl={
                isNL
                  ? '/risk-analysis/selection/subject-selection'
                  : '/risk-analysis/selection/flow-selection'
              }
            />
          )}
          exact
        />

        <Route
          path={[`${url}/selection`, `${url}/selection/:page`]}
          render={() => (
            <SocialRoute>
              <Selection />
            </SocialRoute>
          )}
          exact
        />

        <Route
          path={[`${url}/recommendations/finalize`]}
          render={() => (
            <SocialRoute>
              <RecommendationsFinish />
            </SocialRoute>
          )}
          exact
        />

        <Route
          path={`${url}/recommendations`}
          render={() => (
            <SocialRoute>
              <AdvisoryFlowError />
            </SocialRoute>
          )}
          exact
        />
        <Route
          path={[
            `${url}/recommendations`,
            `${url}/recommendations/:recommendationId`,
          ]}
          render={() => (
            <SocialRoute>
              <Recommendations />
            </SocialRoute>
          )}
          exact
        />
        <Route
          path={[`${url}/recommendations/success/:recommendationId`]}
          render={() =>
            isSocialLoginUrl ? (
              <SocialRoute>
                <CampaignsFinalizationView />
              </SocialRoute>
            ) : (
              <RecommendationsSuccess />
            )
          }
          exact
        />

        <Route
          path={[`${url}/recommendations/company-success/:recommendationId`]}
          render={() => (
            <SocialRoute>
              <CompanySuccess />
            </SocialRoute>
          )}
          exact
        />
      </Switch>
    </>
  );
};

export default RiskAnalysis;
